import { ButtonBase, styled } from '@mui/material';
import {
  getDropdownClearIndicatorStyles,
  getDropdownIndicatorStyles,
  getV2FieldTriggerStyles,
  type V2FieldSize,
} from '@onevision-resources/design-theme';
import { ElementType } from 'react';

/** `field` draws the trigger as a form field box, to sit in a row of inputs. */
export type SelectTriggerVariant = 'outlined' | 'inline' | 'field';

/**
 * Every leading glyph a trigger can carry — a lucide svg, a Material Symbols
 * glyph, a plain avatar, a badged avatar — is measured by this slot rather than
 * by itself, so rows sharing a panel keep one icon footprint.
 */
export const TRIGGER_ICON_SIZE_PX = 21;

export const TRIGGER_ICON_SIZE = `${TRIGGER_ICON_SIZE_PX / 16}rem`;

/** The voice of a property value, shared with rows that render text instead of a trigger. */
export const TRIGGER_FONT_SIZE = '0.78125rem';

export const TRIGGER_LINE_HEIGHT = '1.125rem';

export const SelectTriggerRoot = styled(ButtonBase, {
  name: 'SelectTrigger',
  shouldForwardProp: (prop) =>
    prop !== 'open' &&
    prop !== 'variant' &&
    prop !== 'readOnly' &&
    prop !== 'size' &&
    prop !== 'error',
})<{
  open?: boolean;
  variant?: SelectTriggerVariant;
  readOnly?: boolean;
  size?: V2FieldSize;
  error?: boolean;
  component?: ElementType;
}>(({ theme, open, variant, readOnly, size, error }) => {
  const isInline = variant === 'inline';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    // `min()` keeps the floor from forcing overflow inside narrower containers/viewports.
    minWidth: isInline ? 0 : 'min(9.75rem, 100%)',
    maxWidth: '100%',
    boxSizing: 'border-box',
    height: isInline ? 'auto' : '2rem',
    padding: isInline ? theme.spacing(0.5, 1) : '0 0.5625rem 0 0.6875rem',
    borderRadius: '0.5rem',
    border: isInline ? 'none' : `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
    backgroundColor: isInline ? 'transparent' : theme.palette.custom.OV.sys.controlSurface,
    color: theme.palette.custom.OV.sys.onBackground,
    fontFamily: theme.typography.fontFamily,
    fontSize: TRIGGER_FONT_SIZE,
    fontWeight: isInline ? theme.typography.body2.fontWeight : 600,
    cursor: readOnly ? 'default' : 'pointer',
    // the v2 field box keeps one font size everywhere, so a picker matches the text field beside it
    ...(variant !== 'field' && {
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    }),
    ...(open &&
      !readOnly &&
      (isInline
        ? { backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh }
        : { borderColor: theme.palette.custom.OV.sys.outline })),
    ...(!readOnly && {
      '&:hover': {
        ...(isInline
          ? { backgroundColor: theme.palette.custom.OV.sys.surface }
          : { borderColor: theme.palette.custom.OV.sys.outline }),
      },
      '&:focus-visible': {
        ...(isInline
          ? { outline: `1px solid ${theme.palette.custom.OV.sys.primary}` }
          : { borderColor: theme.palette.custom.OV.sys.primary }),
      },
    }),
    '&:hover [data-trigger-clear], &:focus-visible [data-trigger-clear]': {
      opacity: 1,
    },
    '&.Mui-disabled': {
      opacity: 0.5,
    },
    ...(variant === 'field' && getV2FieldTriggerStyles(theme, { size, error, open })),
  };
});

// Vertical geometry note: line boxes and glyph sizes are pinned so that every
// flex-centered child leaves an EVEN remainder inside its container, i.e. all
// vertical offsets land on whole CSS pixels. Fractional offsets (e.g. a
// ~18.75px "normal" line box in a 22px slot) snap on 1x displays and make
// otherwise-centered rows look off by a pixel.
export const TriggerIconSlot = styled('span', { name: 'SelectTriggerIcon' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: TRIGGER_ICON_SIZE,
  height: TRIGGER_ICON_SIZE,
  fontSize: TRIGGER_ICON_SIZE,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  // The slot dictates the footprint; a badge hanging off an avatar is the one
  // thing allowed to paint outside it.
  '& > *': { width: '100%', height: '100%' },
  '& svg, & .MuiAvatar-root': { width: '100%', height: '100%' },
  '& .MuiBadge-badge > *': { width: 'auto', height: 'auto' },
}));

export const TriggerLabel = styled('span', { name: 'SelectTriggerLabel' })({
  flexGrow: 1,
  minWidth: 0,
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: TRIGGER_LINE_HEIGHT,
});

// Free-form replacement for TriggerLabel (chips, avatars): grows like the
// label but lets its content wrap instead of truncating.
export const TriggerContent = styled('span', { name: 'SelectTriggerContent' })({
  display: 'flex',
  flexGrow: 1,
  minWidth: 0,
  textAlign: 'left',
});

export const TriggerChevron = styled('span', { name: 'SelectTriggerChevron' })(({ theme }) =>
  getDropdownIndicatorStyles(theme),
);

// Lives inside the trigger <button>, so it is a span with a click handler
// rather than a nested button; keyboard users clear with Backspace/Delete on
// the trigger itself. Hidden until the trigger is hovered or focused (see
// SelectTriggerRoot), always visible on touch where hover never fires.
export const TriggerClearButton = styled('span', { name: 'SelectTriggerClear' })(
  ({ theme }) => ({
    ...getDropdownClearIndicatorStyles(theme),
    opacity: 0,
    '@media (hover: none)': { opacity: 1 },
  }),
);

// Same colour as a checked option so the two "this is the current value"
// signals read as one system.
export const TriggerSpinner = styled('span', { name: 'SelectTriggerSpinner' })(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    color: theme.palette.custom.OV.sys.primary,
  }),
);
